// src/context/ServiceContext.tsx — Global state for services, agents, notifications, real-time updates
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// ─── Types ──────────────────────────────────────
interface Service {
    id: number;
    name: string;
    status: string;
    service_type: string;
    project_id: number;
    [key: string]: any;
}

interface Notification {
    id: string;
    type: 'deployment' | 'service' | 'agent' | 'system';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
    serviceId?: number;
}

interface ServiceContextValue {
    services: Service[];
    loading: boolean;
    error: string | null;
    refreshServices: () => Promise<void>;
    notifications: Notification[];
    unreadCount: number;
    markNotificationRead: (id: string) => void;
    markAllRead: () => void;
    addNotification: (type: Notification['type'], title: string, message: string, serviceId?: number) => void;
    wsConnected: boolean;
}

const ServiceContext = createContext<ServiceContextValue | undefined>(undefined);

// ─── Provider ───────────────────────────────────
export const ServiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [services, setServices] = useState<Service[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [wsConnected, setWsConnected] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);

    // ── Load services ──
    const refreshServices = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const data = await api.getServices();
            setServices(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setError(err.message || 'Failed to load services');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) refreshServices();
    }, [user, refreshServices]);

    // ── Notifications ──
    const addNotification = useCallback((type: Notification['type'], title: string, message: string, serviceId?: number) => {
        const notification: Notification = {
            id: `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type,
            title,
            message,
            timestamp: new Date().toISOString(),
            read: false,
            serviceId
        };
        setNotifications(prev => [notification, ...prev].slice(0, 50)); // Max 50
    }, []);

    const markNotificationRead = useCallback((id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }, []);

    const markAllRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    // ── WebSocket for real-time updates ──
    useEffect(() => {
        if (!user) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        // Remove trailing /api and convert protocol
        // Example: https://api.deploynowapp.online/api -> wss://api.deploynowapp.online
        const baseUrl = apiUrl.replace(/\/api$/, '');
        const wsUrl = baseUrl.replace(/^http/, 'ws'); // http->ws, https->wss

        console.log('WebSocket connecting to:', `${wsUrl}/ws/logs`);

        try {
            const ws = new WebSocket(`${wsUrl}/ws/logs`);
            wsRef.current = ws;

            ws.onopen = () => {
                // Authenticate
                ws.send(JSON.stringify({ type: 'auth', token }));
                setWsConnected(true);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'auth_ok') {
                        console.log('🔌 WebSocket authenticated');
                    }

                    if (data.type === 'deployment_complete') {
                        addNotification('deployment',
                            `Deployment ${data.status === 'success' ? 'succeeded' : 'failed'}`,
                            `Deployment #${data.deploymentId} ${data.status}`,
                            undefined
                        );
                        refreshServices(); // Refresh service status
                    }

                    if (data.type === 'service_event') {
                        addNotification('service', data.event || 'Service updated',
                            `Service ${data.serviceId} status changed`,
                            data.serviceId
                        );
                        refreshServices();
                    }

                    if (data.type === 'agent_task_event') {
                        addNotification('agent', `Agent task ${data.status || 'updated'}`,
                            `Agent ${data.agentId} task event`,
                            data.agentId
                        );
                    }
                } catch { /* ignore parse errors */ }
            };

            ws.onclose = () => setWsConnected(false);
            ws.onerror = () => setWsConnected(false);
        } catch { /* WebSocket not supported */ }

        return () => {
            wsRef.current?.close();
            wsRef.current = null;
        };
    }, [user, addNotification, refreshServices]);

    return (
        <ServiceContext.Provider value={{
            services, loading, error, refreshServices,
            notifications, unreadCount, markNotificationRead, markAllRead, addNotification,
            wsConnected
        }}>
            {children}
        </ServiceContext.Provider>
    );
};

export function useServices(): ServiceContextValue {
    const context = useContext(ServiceContext);
    if (context === undefined) {
        throw new Error('useServices must be used within a ServiceProvider');
    }
    return context;
}
